import { useProductList } from "src/hooks/listing";
import {
  NoResultsWrapper,
  PaginationWrapper,
  ProductGridContainer,
  StyledAuthBanner,
} from "./ProductList.styled";
import { ProductsGrid } from "src/atoms/ProductsGrid";
import { generateProductCardProps } from "src/utils/product-listing-helpers";
import { ProductCardWishlist } from "../ProductCardWishlist";
import { SeparatorMessage } from "src/molecules/SeparatorMessage";
import { t, Trans } from "@lingui/macro";
import { NoProductsFound } from "src/molecules/NoProductsFound";
import { Button } from "src/atoms/Button";
import { Flex, useBreakpoints } from "@otrium/core";
import { useLingui } from "@lingui/react";
import { GridSkeleton } from "./GridSkeleton";
import {
  Fragment,
  MouseEvent,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { Promotion } from "./Promotion";
import useFilterProductLabels from "src/hooks/useFilterProductLabels";
import { PageContentType } from "src/types/graphql.d";
import { AuthModule } from "../AuthModule";
import {
  localeFixOptional,
  ShopType,
} from "src/utils/genderLocale/genderLocale";
import { useNewIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { useItemListSchema } from "src/hooks/useItemListSchema";
import type { Gender, ProductListType } from "src/types/product-listing";
import { trackProductClickedEvent } from "src/tracking/events/product-list-item-selected";
import { SEGMENT_LIST_FORMAT } from "src/segment";
import { PRODUCT_LIST_CONTEXT_KEYS } from "src/utils/product-list-context-keys";
import { useSegmentProductList } from "src/hooks/useSegmentProductList";
import { BREAKPOINTS } from "src/breakpoints";

type GridProps = {
  slug: string;
  categoryId?: string;
  /** Dual-run fallback identifier — see ListingParams.collectionId. */
  collectionId?: string;
  facetValue: string;
  gender?: Gender;
  pageContentType: ProductListType;
  title?: string;
  formattedTitle?: boolean;
  hitsPerPage: number;
  gridRef: MutableRefObject<HTMLDivElement | null>;
  hideCategoryTree?: boolean;
  /** sameAs links for brand schema enrichment (Wikidata, official site, socials) */
  brandSameAs?: string[];
};

// Measured against the live grid rather than derived from the column count:
// the tile is ~45vw at 2 columns, ~30vw at 3 columns while the category tree
// is still collapsed, ~23vw once the tree takes its column, and a flat ~290px
// beyond the 1264px content cap. ProductCard's shared default ends in 420px —
// the width 1264/3 implies if you ignore the sidebar — which made every
// desktop tile fetch the 1080 candidate where 640 covers it.
//
// The tree-less COLLECTIONS_PLP layout is 4 columns of ~298px, close enough to
// the same candidates, so one expression serves both.
const PLP_CONTENT_MAX_WIDTH = 1264;
const PLP_DESKTOP_TILE_WIDTH = 290;
const PLP_IMAGE_SIZES = [
  `(max-width: ${BREAKPOINTS.sm}) 45vw`,
  `(max-width: ${BREAKPOINTS.md}) 30vw`,
  `(max-width: ${PLP_CONTENT_MAX_WIDTH}px) 23vw`,
  `${PLP_DESKTOP_TILE_WIDTH}px`,
].join(", ");

const NO_PADDING_PLP_TYPES: ProductListType[] = ["COLLECTIONS_PLP"];
const EXCLUDED_PROMOTIONS_PLPS: ProductListType[] = [
  "SEARCH_PLP",
  "COLLECTIONS_PLP",
];

function Grid({
  pageContentType,
  title,
  slug,
  categoryId,
  collectionId,
  facetValue,
  hitsPerPage,
  hideCategoryTree,
  brandSameAs,
  gender,
}: GridProps): JSX.Element {
  const isFirstRender = useRef(true);

  const {
    products,
    loadMore,
    loading,
    nextPageHref,
    hasNextPage,
    hasPreviousPage,
    loadPrevious,
    totalResults,
    resultId,
    hasFetched,
  } = useProductList({
    facetValue,
    categoryId,
    collectionId,
    productListType: pageContentType,
    gender,
  });

  const { isLoggedIn, isSessionLoading } = useNewIsLoggedIn();

  const { i18n } = useLingui();
  const { isMobile } = useBreakpoints();

  const { data: labels } = useFilterProductLabels({
    products,
    pageContentType:
      pageContentType === "BRAND_PLP"
        ? PageContentType.Brand
        : PageContentType.Category,
  });

  const handleProductClick = useCallback(
    (itemId: string) => {
      const idx = products?.findIndex((p) => p.id === itemId);

      if (idx === -1) {
        return;
      }

      const product = generateProductCardProps(products[idx], labels);

      void trackProductClickedEvent({
        product,
        position: idx + 1,
        productListMeta: {
          listFormat: SEGMENT_LIST_FORMAT.default,
          listId: title || "",
          listType: PRODUCT_LIST_CONTEXT_KEYS[pageContentType],
          shopType: localeFixOptional({ gender }) ?? ShopType.All,
        },
        resultId,
      });
    },
    [products, labels, title, pageContentType, gender, resultId]
  );

  const { setVisibleProducts } = useSegmentProductList({
    listFormat: SEGMENT_LIST_FORMAT.default,
    listId: title || "",
    listType: PRODUCT_LIST_CONTEXT_KEYS[pageContentType],
    numberOfItems: totalResults,
    shopType: localeFixOptional({ gender }) ?? ShopType.All,
    resultId,
  });

  // Generate ItemList schema for the product listing (replaces OfferCatalog)
  useItemListSchema({
    products,
    productListType: pageContentType,
    title,
    description: title ? `Shop ${title} at Otrium` : undefined,
    slug: slug || "",
    gender,
    brandSameAs,
  });

  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  const authBannerInsertPosition = useMemo(
    () => (isMobile ? 2 : hideCategoryTree ? 4 : 3),
    [isMobile, hideCategoryTree]
  );

  const handleShowMore = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      loadMore();
    },
    [loadMore]
  );

  const total = useMemo(() => totalResults, [totalResults]);

  return (
    <ProductGridContainer
      className={hideCategoryTree ? "hideCategoryTree" : ""}
    >
      {slug &&
      !EXCLUDED_PROMOTIONS_PLPS.includes(pageContentType) &&
      !!localeFixOptional({ gender }) ? (
        <Promotion slug={slug} productListType={pageContentType} />
      ) : null}

      {!total && !hasFetched ? (
        <ProductsGrid
          hideCategoryTree={hideCategoryTree}
          data-product-list={pageContentType}
        >
          <GridSkeleton numberOfItems={hitsPerPage} />
        </ProductsGrid>
      ) : null}

      {!total && hasFetched ? (
        <NoResultsWrapper>
          <NoProductsFound />
        </NoResultsWrapper>
      ) : null}

      {total ? (
        <>
          {!isFirstRender.current && hasPreviousPage ? (
            <Flex variant="centered" marginBlockEnd={2}>
              <Button variant="secondary" onClick={loadPrevious}>
                <Trans>show_previous_products</Trans>
              </Button>
            </Flex>
          ) : null}
          <ProductsGrid
            hideCategoryTree={hideCategoryTree}
            data-product-list={pageContentType}
          >
            {products.map((product, idx) => {
              const shouldInsertAuthBar =
                (idx + 1 - authBannerInsertPosition) % hitsPerPage === 0;

              return (
                <Fragment key={product?.id || `product-grid-item-${idx}`}>
                  <ProductCardWishlist
                    position={idx}
                    product={product}
                    onProductClickTracker={handleProductClick}
                    setVisibleProducts={setVisibleProducts}
                    resultId={resultId}
                    imageSizes={PLP_IMAGE_SIZES}
                  />
                  {shouldInsertAuthBar && !isSessionLoading && !isLoggedIn && (
                    <StyledAuthBanner hideCategoryTree={hideCategoryTree}>
                      <AuthModule
                        trackingData={{
                          categorySlug: slug,
                          pageType: pageContentType,
                          shopType: localeFixOptional({ gender }),
                        }}
                        noMarging
                      />
                    </StyledAuthBanner>
                  )}
                </Fragment>
              );
            })}

            {loading && !isFirstRender.current ? (
              <GridSkeleton numberOfItems={hitsPerPage} />
            ) : null}
          </ProductsGrid>

          <PaginationWrapper
            className={`${
              NO_PADDING_PLP_TYPES.includes(pageContentType)
                ? "no-padding-plp"
                : ""
            }`}
          >
            <SeparatorMessage data-testid="total-products">
              {t(i18n)`Seen ${products.length} from ${total}`}
            </SeparatorMessage>

            {hasNextPage ? (
              <a
                href={nextPageHref ?? undefined}
                onClick={handleShowMore}
                data-testid="pagination-show-next"
              >
                <Button variant="secondary">
                  <Trans>Show next products</Trans>
                </Button>
              </a>
            ) : null}
          </PaginationWrapper>
        </>
      ) : null}
    </ProductGridContainer>
  );
}

export { Grid };
